import { GS1_AI, type PrimaryKeyAI } from '@myplayer/gs1';
import { type IdentifierRecord, type SectorId, type StagedDraftRecord } from '@myplayer/schema';
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import {
  canUseAllWorkspacesScope,
  type AdminUserSession,
  type WorkspaceOption,
  type WorkspaceScopeMode,
} from './auth-context.js';
import type * as fb from './firebase.js';
import { getFirebaseClients } from './firebase.js';
import type {
  DiscardIdentifierDraftCallablePayload,
  DiscardIdentifierDraftCallableResult,
} from './product-review-changes.js';
import type { ProductSortOption } from './products-helpers.js';
import type { ProductStatusFilter, ProductValidationFilter } from './products-table.js';

export * from './resolver-host-helpers.js';

export interface ProductsControllerParams {
  readonly initialRecords?: readonly IdentifierRecord[] | undefined;
  readonly initialSelectedKey?: string | null | undefined;
  readonly initialEditMode?: boolean | undefined;
  readonly resolverHost?: string | undefined;
  readonly defaultViewMode?: 'cards' | 'table' | undefined;
  readonly defaultPageSize?: number | undefined;
  readonly onStageDraftLinks?:
    | ((params: {
        readonly identifierKey: string;
        readonly draft: StagedDraftRecord;
        readonly sector?: SectorId | undefined;
      }) => Promise<IdentifierRecord | void> | IdentifierRecord | void)
    | undefined;
  readonly onDiscardDraft?:
    | ((
        payload: DiscardIdentifierDraftCallablePayload,
      ) => Promise<DiscardIdentifierDraftCallableResult>)
    | undefined;
  readonly onSetRecall?:
    ((payload: fb.SetRecallCallablePayload) => Promise<fb.SetRecallCallableResult>) | undefined;
  readonly onClearRecall?:
    ((payload: fb.ClearRecallCallablePayload) => Promise<fb.ClearRecallCallableResult>) | undefined;
  readonly onSetScheduledChange?:
    | ((
        payload: fb.SetScheduledChangeCallablePayload,
      ) => Promise<fb.SetScheduledChangeCallableResult>)
    | undefined;
  readonly onCancelScheduledChange?:
    | ((
        payload: fb.CancelScheduledChangeCallablePayload,
      ) => Promise<fb.CancelScheduledChangeCallableResult>)
    | undefined;
}

export interface UseWorkspaceIdentifiersOptions {
  readonly workspaceIds?: readonly string[];
  readonly allWorkspaceIds?: readonly string[];
}

export function useWorkspaceIdentifiers(
  session: AdminUserSession | null,
  initialRecords: readonly IdentifierRecord[] | undefined,
  initialSelectedKey?: string | null,
  options?: UseWorkspaceIdentifiersOptions,
) {
  const [records, setRecords] = useState<readonly IdentifierRecord[]>(initialRecords ?? []);
  const [allOrgRecords, setAllOrgRecords] = useState<readonly IdentifierRecord[]>(
    initialRecords ?? [],
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(initialSelectedKey ?? null);
  const [loading, setLoading] = useState<boolean>(initialRecords === undefined && session !== null);
  const [listenerError, setListenerError] = useState<string | null>(null);

  const targetWorkspaceIdsKey =
    options?.workspaceIds && options.workspaceIds.length > 0
      ? Array.from(new Set(options.workspaceIds)).sort().join(',')
      : (session?.activeWorkspaceId ?? '');

  const allWorkspaceIdsKey =
    options?.allWorkspaceIds && options.allWorkspaceIds.length > 0
      ? Array.from(new Set(options.allWorkspaceIds)).sort().join(',')
      : targetWorkspaceIdsKey;

  useEffect(() => {
    if (initialRecords !== undefined) {
      setRecords(initialRecords);
      setAllOrgRecords(initialRecords);
      setLoading(false);
      return;
    }
    if (!session) {
      setLoading(false);
      return;
    }
    const targetWsIds = targetWorkspaceIdsKey
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const allWsIds = Array.from(
      new Set([
        ...targetWsIds,
        ...allWorkspaceIdsKey
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      ]),
    ).sort();
    if (targetWsIds.length === 0) {
      setRecords([]);
      setAllOrgRecords([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { db } = getFirebaseClients();
    const byWorkspace = new Map<string, readonly IdentifierRecord[]>();
    const settledWorkspaces = new Set<string>();

    const recomputeRecords = (): void => {
      const mergedTarget: IdentifierRecord[] = [];
      for (const wsId of targetWsIds) {
        const wsDocs = byWorkspace.get(wsId) ?? [];
        for (const docRecord of wsDocs) {
          mergedTarget.push(docRecord);
        }
      }
      mergedTarget.sort(
        (a, b) =>
          a.identifierKey.localeCompare(b.identifierKey) ||
          (a.workspaceId ?? '').localeCompare(b.workspaceId ?? ''),
      );
      const mergedAll: IdentifierRecord[] = [];
      for (const wsId of allWsIds) {
        const wsDocs = byWorkspace.get(wsId) ?? [];
        for (const docRecord of wsDocs) {
          mergedAll.push(docRecord);
        }
      }
      mergedAll.sort(
        (a, b) =>
          a.identifierKey.localeCompare(b.identifierKey) ||
          (a.workspaceId ?? '').localeCompare(b.workspaceId ?? ''),
      );
      setRecords(mergedTarget);
      setAllOrgRecords(mergedAll);
      setSelectedKey((prev) =>
        prev !== null && mergedTarget.some((d) => d.identifierKey === prev) ? prev : null,
      );
      if (settledWorkspaces.size >= targetWsIds.length) {
        setLoading(false);
      }
    };

    const unsubscribes = allWsIds.map((wsId) => {
      const colRef = collection(
        db,
        'organisations',
        session.orgId,
        'workspaces',
        wsId,
        'identifiers',
      );
      return onSnapshot(
        colRef,
        (snapshot) => {
          setListenerError(null);
          const docs = snapshot.docs.map(
            (docSnap) =>
              ({
                identifierKey: docSnap.id,
                workspaceId: wsId,
                ...docSnap.data(),
              }) as IdentifierRecord,
          );
          byWorkspace.set(wsId, docs);
          settledWorkspaces.add(wsId);
          recomputeRecords();
        },
        (err) => {
          setListenerError(err.message);
          settledWorkspaces.add(wsId);
          recomputeRecords();
        },
      );
    });

    return () => {
      for (const unsub of unsubscribes) {
        unsub();
      }
    };
  }, [session?.orgId, targetWorkspaceIdsKey, allWorkspaceIdsKey, initialRecords]);

  return {
    records,
    allOrgRecords,
    setRecords,
    selectedKey,
    setSelectedKey,
    loading,
    listenerError,
  };
}

export function useWorkspaceTargetMeta(
  session: AdminUserSession | null,
  availableWorkspaces: readonly WorkspaceOption[],
  workspaceScope: WorkspaceScopeMode,
  workspaceFilter: string,
) {
  const canViewAllWorkspaces = canUseAllWorkspacesScope(session, availableWorkspaces);
  const isMultiWorkspaceQuery =
    canViewAllWorkspaces && (workspaceScope === 'ALL' || workspaceFilter !== 'ALL');

  const targetWorkspaceIds = useMemo<readonly string[]>(() => {
    if (!session) return [];
    return isMultiWorkspaceQuery && availableWorkspaces.length > 0
      ? availableWorkspaces.map((ws) => ws.workspaceId)
      : [session.activeWorkspaceId];
  }, [session, isMultiWorkspaceQuery, availableWorkspaces]);

  const allOrgWorkspaceIds = useMemo<readonly string[]>(() => {
    if (!session) return [];
    return Array.from(
      new Set([
        ...availableWorkspaces.map((ws) => ws.workspaceId),
        ...(session.activeWorkspaceId ? [session.activeWorkspaceId] : []),
      ]),
    ).sort();
  }, [session, availableWorkspaces]);

  const workspaceNameMap = useMemo<ReadonlyMap<string, string>>(() => {
    const map = new Map<string, string>(availableWorkspaces.map((ws) => [ws.workspaceId, ws.name]));
    if (session && !map.has(session.activeWorkspaceId)) {
      map.set(session.activeWorkspaceId, session.activeWorkspaceId);
    }
    return map;
  }, [availableWorkspaces, session]);

  return {
    canViewAllWorkspaces,
    isMultiWorkspaceQuery,
    targetWorkspaceIds,
    allOrgWorkspaceIds,
    workspaceNameMap,
  };
}

const MOBILE_VIEWPORT_MAX_WIDTH_PX = 768;

export function resolveDefaultProductsViewMode(explicit?: 'cards' | 'table'): 'cards' | 'table' {
  if (explicit !== undefined) return explicit;
  if (typeof window !== 'undefined') {
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia(`(max-width: ${String(MOBILE_VIEWPORT_MAX_WIDTH_PX - 1)}px)`).matches
    ) {
      return 'cards';
    }
    if (typeof window.innerWidth === 'number' && window.innerWidth > 0) {
      return window.innerWidth < MOBILE_VIEWPORT_MAX_WIDTH_PX ? 'cards' : 'table';
    }
  }
  return 'table';
}

export function useResponsiveProductsViewMode(explicit?: 'cards' | 'table'): {
  readonly viewMode: 'cards' | 'table';
  readonly setViewMode: (nextMode: 'cards' | 'table') => void;
} {
  const [viewMode, setViewModeState] = useState<'cards' | 'table'>(() =>
    resolveDefaultProductsViewMode(explicit),
  );
  const [userOverrodeViewMode, setUserOverrodeViewMode] = useState(explicit !== undefined);
  const setViewMode = (nextMode: 'cards' | 'table'): void => {
    setUserOverrodeViewMode(true);
    setViewModeState(nextMode);
  };
  useEffect(() => {
    if (userOverrodeViewMode || typeof window === 'undefined') return;
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(`(max-width: ${String(MOBILE_VIEWPORT_MAX_WIDTH_PX - 1)}px)`);
    const handleViewportChange = (event: MediaQueryListEvent): void => {
      setViewModeState(event.matches ? 'cards' : 'table');
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handleViewportChange);
      return () => mql.removeEventListener('change', handleViewportChange);
    }
    return undefined;
  }, [userOverrodeViewMode]);
  return { viewMode, setViewMode };
}

export function useProductsFilterAndCreateState(
  activeWorkspaceId?: string,
  defaultViewMode?: 'cards' | 'table',
) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const { viewMode, setViewMode } = useResponsiveProductsViewMode(defaultViewMode);
  const [brandFilter, setBrandFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<ProductStatusFilter>('ALL');
  const [sectorFilter, setSectorFilter] = useState<'ALL' | SectorId>('ALL');
  const [validationFilter, setValidationFilter] = useState<ProductValidationFilter>('ALL');
  const [sortBy, setSortBy] = useState<ProductSortOption | undefined>(undefined);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newAi, setNewAi] = useState<PrimaryKeyAI>(GS1_AI.GTIN);
  const [newKeyInput, setNewKeyInput] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [newDefaultUrl, setNewDefaultUrl] = useState('https://brand.example.com/products/item');
  const [newWorkspaceId, setNewWorkspaceId] = useState<string>(activeWorkspaceId ?? '');
  const [newFormError, setNewFormError] = useState<string | null>(null);
  const [creatingProduct, setCreatingProduct] = useState(false);

  useEffect(() => {
    if (activeWorkspaceId) setNewWorkspaceId(activeWorkspaceId);
  }, [activeWorkspaceId, showNewForm]);

  return {
    searchQuery,
    setSearchQuery,
    showSearchBar,
    setShowSearchBar,
    viewMode,
    setViewMode,
    brandFilter,
    setBrandFilter,
    statusFilter,
    setStatusFilter,
    sectorFilter,
    setSectorFilter,
    validationFilter,
    setValidationFilter,
    sortBy,
    setSortBy,
    showNewForm,
    setShowNewForm,
    newAi,
    setNewAi,
    newKeyInput,
    setNewKeyInput,
    newTitle,
    setNewTitle,
    newBrand,
    setNewBrand,
    newDefaultUrl,
    setNewDefaultUrl,
    newWorkspaceId,
    setNewWorkspaceId,
    newFormError,
    setNewFormError,
    creatingProduct,
    setCreatingProduct,
  };
}
